"use client";

import type { PropsWithChildren, ReactElement } from "react";
import React from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import classNames from "classnames";

interface IMenu {
  path: { pathname: string };
  icon: JSX.Element;
  iconActive: JSX.Element;
  compareActivePaths: string[];
}

export const PageWithNavBar = ({ className, children }: PropsWithChildren<{ className?: string }>): ReactElement => {
  const pathname = usePathname();

  const menus: IMenu[] = [
    { path: { pathname: "/" }, icon: <NameBook />, iconActive: <NameBook />, compareActivePaths: ["/itineraries"] },
    {
      path: { pathname: "/discover" },
      icon: <NameSearchGrey />,
      iconActive: <NameSearchGrey />,
      compareActivePaths: ["/discover", "/guides"],
    },
    {
      path: { pathname: "/saved" },
      icon: <NameFavoriteGrey />,
      iconActive: <NameFavoriteGreyActive />,
      compareActivePaths: ["/saved"],
    },
    {
      path: { pathname: "/profile" },
      icon: <NamePerson />,
      iconActive: <NamePersonActive />,
      compareActivePaths: ["/profile"],
    },
  ];

  return (
    <div
      className={classNames("flex h-screen flex-col bg-variable-collection-neutral-200 md:flex-col-reverse", className)}
    >
      <div className="flex-1 overflow-auto">{children}</div>
      <div className="sticky inset-x-0 bottom-0 z-10 flex justify-between border-t-[1px] border-variable-collection-neutral-200 bg-variable-collection-white px-14 pb-9 pt-4 backdrop-blur md:justify-center md:gap-16 md:bg-variable-collection-neutral-200 md:py-8">
        {menus.map((menu, index) => {
          const activedPath = menu.compareActivePaths.find((a) => pathname.startsWith(a));
          const isActived = activedPath !== null && activedPath !== undefined;
          return (
            <button
              key={index}
              type="button"
              className={classNames({
                "opacity-30": !isActived,
              })}
            >
              <Link href={menu.path}>{isActived ? menu.iconActive : menu.icon}</Link>
            </button>
          );
        })}
      </div>
    </div>
  );
};

const NameBook = (): JSX.Element => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28" fill="none">
      <mask id="mask0_1701_869" maskUnits="userSpaceOnUse" x="0" y="0" width="28" height="28">
        <rect width="28" height="28" fill="#D9D9D9" />
      </mask>
      <g mask="url(#mask0_1701_869)">
        <path
          d="M7.83462 26.0167C6.87247 26.0167 6.04561 25.6905 5.35405 25.0379C4.66247 24.3854 4.31668 23.5779 4.31668 22.6154V6.25072C4.31668 5.36526 4.63603 4.60431 5.27472 3.96787C5.91339 3.33146 6.67546 2.9235 7.56092 2.74401L15.1891 1.2498C16.0745 1.08976 16.8673 1.29201 17.5673 1.85656C18.2673 2.42113 18.6173 3.15519 18.6173 4.05876V17.2151C18.6173 17.8868 18.4008 18.4822 17.9678 19.0011C17.5348 19.5201 16.9787 19.8573 16.2997 20.0129L7.80545 21.7494C7.56164 21.7958 7.33728 21.8908 7.13237 22.0343C6.92745 22.1779 6.82498 22.3716 6.82498 22.6154C6.82498 22.8742 6.92557 23.0877 7.12674 23.256C7.32793 23.4243 7.56389 23.5084 7.83462 23.5084H20.816C20.9207 23.5084 21.0067 23.4747 21.074 23.4074C21.1413 23.3401 21.175 23.2541 21.175 23.1494V5.57093C21.175 5.22542 21.2976 4.93002 21.5429 4.68473C21.7882 4.43942 22.0836 4.31676 22.4291 4.31676C22.7746 4.31676 23.07 4.43942 23.3153 4.68473C23.5606 4.93002 23.6833 5.22542 23.6833 5.57093V23.1494C23.6833 23.9646 23.4092 24.6462 22.861 25.1944C22.3128 25.7426 21.6311 26.0167 20.816 26.0167L7.83462 26.0167ZM9.71546 18.8688C10.0111 18.8065 10.2532 18.6616 10.4416 18.4341C10.6301 18.2067 10.7243 17.9462 10.7243 17.6527V6.22377C10.7243 5.8238 10.5751 5.49908 10.2767 5.2496C9.97835 5.00013 9.62773 4.91947 9.22488 5.00761C8.92922 5.06997 8.68717 5.21487 8.49871 5.44231C8.31026 5.66975 8.21603 5.93024 8.21603 6.22377V17.6527C8.21603 18.0527 8.36523 18.3774 8.66362 18.6269C8.96202 18.8763 9.31263 18.957 9.71546 18.8688Z"
          fill="black"
        />
      </g>
    </svg>
  );
};

const NameSearchGrey = (): JSX.Element => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="29" height="28" viewBox="0 0 29 28" fill="none">
      <mask id="mask0_1701_872" maskUnits="userSpaceOnUse" x="0" y="0" width="29" height="28">
        <rect x="0.666672" width="28" height="28" fill="#D9D9D9" />
      </mask>
      <g mask="url(#mask0_1701_872)">
        <path
          d="M12.7416 19.6808C14.861 19.6808 16.6548 18.9457 18.1228 17.4754C19.5909 16.0052 20.3249 14.2088 20.3249 12.0863C20.3249 9.96369 19.5909 8.16729 18.1228 6.69705C16.6548 5.22682 14.861 4.4917 12.7416 4.4917C10.6221 4.4917 8.82839 5.30946 7.36034 6.94497C5.89228 8.58048 5.15825 10.2942 5.15825 12.0863C5.15825 14.2088 5.89228 16.0052 7.36034 17.4754C8.82839 18.9457 10.6221 19.6808 12.7416 19.6808ZM12.7416 22.1667C9.9489 22.1667 7.56879 21.1831 5.60126 19.2159C3.63372 17.2487 2.64995 14.869 2.64995 12.0768C2.64995 9.28461 3.63355 6.90433 5.60074 4.93597C7.56792 2.96759 9.9476 1.9834 12.7398 1.9834C15.532 1.9834 17.9123 2.96717 19.8807 4.93472C21.849 6.90224 22.8332 9.27877 22.8332 12.0643C22.8332 13.2642 22.6365 14.4049 22.243 15.4863C21.8496 16.5676 21.2999 17.541 20.5941 18.4064L25.788 23.3962C26.0468 23.633 26.1762 23.9229 26.1762 24.2659C26.1762 24.609 26.0468 24.9117 25.788 25.1742C25.5487 25.4173 25.266 25.5369 24.94 25.5332C24.6139 25.5294 24.3215 25.4079 24.0627 25.1686L18.7925 20.1317C17.9592 20.7725 17.026 21.2719 15.9929 21.6298C14.9598 21.9877 13.876 22.1667 12.7416 22.1667Z"
          fill="black"
        />
      </g>
    </svg>
  );
};

const NameFavoriteGrey = (): JSX.Element => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="29" height="28" viewBox="0 0 29 28" fill="none">
      <mask id="mask0_1701_875" maskUnits="userSpaceOnUse" x="0" y="0" width="29" height="28">
        <rect x="0.333374" width="28" height="28" fill="#D9D9D9" />
      </mask>
      <g mask="url(#mask0_1701_875)">
        <path
          d="M14.3221 23.7236C13.9557 23.7236 13.6076 23.6593 13.2778 23.5307C12.948 23.402 12.6484 23.2128 12.3792 22.9631L10.2658 21.0314C8.29441 19.223 6.47673 17.3848 4.81273 15.5166C3.14873 13.6484 2.31673 11.5679 2.31673 9.27497C2.31673 7.38659 2.96251 5.80616 4.25407 4.5337C5.54563 3.26123 7.13119 2.625 9.01075 2.625C10.0777 2.625 11.0701 2.85647 11.9877 3.3194C12.9053 3.78233 13.6872 4.37502 14.3334 5.09746C14.9795 4.37502 15.7614 3.78233 16.679 3.3194C17.5967 2.85647 18.589 2.625 19.656 2.625C21.5356 2.625 23.1211 3.26123 24.4127 4.5337C25.7042 5.80616 26.35 7.38659 26.35 9.27497C26.35 11.5903 25.4993 13.6877 23.7979 15.5671C22.0965 17.4465 20.3073 19.2544 18.4301 20.991L16.2763 22.9631C16.0071 23.2128 15.7057 23.402 15.3721 23.5307C15.0386 23.6593 14.6886 23.7236 14.3221 23.7236ZM13.1443 7.80319C12.7464 7.07476 12.1889 6.44768 11.4717 5.92194C10.7545 5.39618 9.93893 5.1333 9.02504 5.1333C7.82504 5.1333 6.82505 5.52219 6.02504 6.29997C5.22504 7.07775 4.82504 8.07081 4.82504 9.27914C4.82504 10.2516 5.13166 11.2534 5.7449 12.2847C6.35814 13.316 7.13029 14.3511 8.06137 15.3898C8.99247 16.4286 9.99947 17.4345 11.0824 18.4075C12.1653 19.3804 13.1704 20.2902 14.0978 21.1368C14.1651 21.1967 14.2436 21.2266 14.3334 21.2266C14.4231 21.2266 14.5017 21.1967 14.569 21.1368C15.4963 20.2902 16.5015 19.3804 17.5844 18.4075C18.6673 17.4345 19.6743 16.4286 20.6054 15.3898C21.5364 14.3511 22.3086 13.316 22.9218 12.2847C23.5351 11.2534 23.8417 10.2516 23.8417 9.27914C23.8417 8.07081 23.4417 7.07775 22.6417 6.29997C21.8417 5.52219 20.8417 5.1333 19.6417 5.1333C18.7278 5.1333 17.9074 5.39618 17.1805 5.92194C16.4535 6.44768 15.9009 7.07476 15.5225 7.80319C15.4043 8.03054 15.238 8.20592 15.0236 8.32933C14.8093 8.45272 14.5857 8.51442 14.3528 8.51442C14.12 8.51442 13.8948 8.45272 13.6771 8.32933C13.4595 8.20592 13.2819 8.03054 13.1443 7.80319Z"
          fill="black"
        />
      </g>
    </svg>
  );
};

const NameFavoriteGreyActive = (): JSX.Element => {
  return (
    <svg width="29" height="28" viewBox="0 0 29 28" fill="none" xmlns="http://www.w3.org/2000/svg">
      <mask id="mask0_3521_67019" maskUnits="userSpaceOnUse" x="0" y="0" width="29" height="28">
        <rect x="0.333008" width="28" height="28" fill="#D9D9D9" />
      </mask>
      <g mask="url(#mask0_3521_67019)">
        <path
          d="M14.3327 24.5L12.641 22.9833C10.6771 21.2139 9.05352 19.6875 7.77018 18.4042C6.48685 17.1208 5.46602 15.9688 4.70768 14.9479C3.94935 13.9271 3.41949 12.9889 3.1181 12.1333C2.81671 11.2778 2.66602 10.4028 2.66602 9.50834C2.66602 7.68056 3.27852 6.15417 4.50352 4.92917C5.72852 3.70417 7.2549 3.09167 9.08268 3.09167C10.0938 3.09167 11.0563 3.30556 11.9702 3.73334C12.8841 4.16112 13.6716 4.7639 14.3327 5.54167C14.9938 4.7639 15.7813 4.16112 16.6952 3.73334C17.6091 3.30556 18.5716 3.09167 19.5827 3.09167C21.4105 3.09167 22.9368 3.70417 24.1618 4.92917C25.3869 6.15417 25.9993 7.68056 25.9993 9.50834C25.9993 10.4028 25.8487 11.2778 25.5473 12.1333C25.2459 12.9889 24.716 13.9271 23.9577 14.9479C23.1993 15.9688 22.1785 17.1208 20.8952 18.4042C19.6118 19.6875 17.9882 21.2139 16.0243 22.9833L14.3327 24.5Z"
          fill="black"
        />
      </g>
    </svg>
  );
};

const NamePerson = (): JSX.Element => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28" fill="none">
      <mask id="mask0_1701_878" maskUnits="userSpaceOnUse" x="0" y="0" width="28" height="28">
        <rect width="28" height="28" fill="#D9D9D9" />
      </mask>
      <g mask="url(#mask0_1701_878)">
        <path
          d="M14 13.291C12.6827 13.291 11.5658 12.8328 10.6495 11.9165C9.73319 11.0002 9.27503 9.88338 9.27503 8.56604C9.27503 7.24868 9.73319 6.13184 10.6495 5.21554C11.5658 4.29922 12.6827 3.84106 14 3.84106C15.3173 3.84106 16.4342 4.29922 17.3505 5.21554C18.2668 6.13184 18.725 7.24868 18.725 8.56604C18.725 9.88338 18.2668 11.0002 17.3505 11.9165C16.4342 12.8328 15.3173 13.291 14 13.291ZM4.37503 21.774V20.807C4.37503 20.0996 4.55148 19.4601 4.90437 18.8886C5.25727 18.317 5.72847 17.8781 6.31797 17.5718C7.54895 16.9481 8.80859 16.4803 10.0969 16.1684C11.3851 15.8566 12.6853 15.7007 13.9973 15.7007C15.3093 15.7007 16.6104 15.8566 17.9005 16.1684C19.1905 16.4803 20.451 16.9481 21.682 17.5718C22.2715 17.8781 22.7427 18.317 23.0956 18.8886C23.4485 19.4601 23.625 20.0996 23.625 20.807V21.774C23.625 22.4994 23.3845 23.1026 22.9037 23.5834C22.4228 24.0643 21.8197 24.3048 21.0942 24.3048H6.90576C6.18035 24.3048 5.57721 24.0643 5.09635 23.5834C4.61547 23.1026 4.37503 22.4994 4.37503 21.774ZM6.88333 21.7964H21.1167V20.807C21.1167 20.5707 21.0677 20.3714 20.9697 20.2091C20.8717 20.0468 20.7345 19.9216 20.558 19.8333C19.5334 19.3382 18.4551 18.9437 17.3229 18.6498C16.1907 18.3559 15.0831 18.2089 14 18.2089C12.9169 18.2089 11.8093 18.351 10.6771 18.6352C9.54493 18.9194 8.46656 19.3188 7.44199 19.8333C7.26549 19.9021 7.12826 20.0225 7.0303 20.1945C6.93232 20.3665 6.88333 20.5707 6.88333 20.807V21.7964ZM14 10.7827C14.6222 10.7827 15.1472 10.5688 15.575 10.141C16.0028 9.71326 16.2167 9.18826 16.2167 8.56604C16.2167 7.94381 16.0028 7.41881 15.575 6.99104C15.1472 6.56326 14.6222 6.34937 14 6.34937C13.3778 6.34937 12.8528 6.56326 12.425 6.99104C11.9972 7.41881 11.7833 7.94381 11.7833 8.56604C11.7833 9.18826 11.9972 9.71326 12.425 10.141C12.8528 10.5688 13.3778 10.7827 14 10.7827Z"
          fill="black"
        />
      </g>
    </svg>
  );
};

const NamePersonActive = (): JSX.Element => {
  return (
    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
      <mask id="mask0_3543_650" maskUnits="userSpaceOnUse" x="0" y="0" width="28" height="28">
        <rect width="28" height="28" fill="#D9D9D9" />
      </mask>
      <g mask="url(#mask0_3543_650)">
        <path
          d="M14 13.2909C12.6793 13.2909 11.5617 12.8336 10.647 11.9189C9.73233 11.0045 9.275 9.8868 9.275 8.56594C9.275 7.24528 9.73233 6.12761 10.647 5.21294C11.5617 4.29828 12.6793 3.84094 14 3.84094C15.3207 3.84094 16.4383 4.29828 17.353 5.21294C18.2677 6.12761 18.725 7.24528 18.725 8.56594C18.725 9.8868 18.2677 11.0045 17.353 11.9189C16.4383 12.8336 15.3207 13.2909 14 13.2909ZM4.375 21.7741V20.8069C4.375 20.0995 4.54961 19.4586 4.89883 18.8842C5.24806 18.3098 5.72114 17.8723 6.31808 17.5717C7.54892 16.9482 8.8094 16.4804 10.0995 16.1685C11.3897 15.8566 12.6898 15.7007 14 15.7007C15.3102 15.7007 16.6103 15.8566 17.9005 16.1685C19.1906 16.4804 20.4511 16.9482 21.6819 17.5717C22.2789 17.8723 22.7519 18.3098 23.1012 18.8842C23.4504 19.4586 23.625 20.0995 23.625 20.8069V21.7741C23.625 22.4993 23.3846 23.1025 22.9037 23.5836C22.4228 24.0644 21.8197 24.3049 21.0942 24.3049H6.90579C6.18032 24.3049 5.57715 24.0644 5.09629 23.5836C4.61543 23.1025 4.375 22.4993 4.375 21.7741Z"
          fill="black"
        />
      </g>
    </svg>
  );
};
